import { useEffect, useMemo, useState } from "react"
import { Link, useHistory, useLocation } from "react-router-dom"
import { Anchor, Button, Center, Divider, Group, Loader, Menu, Select, Stack, Text, Title, useMantineColorScheme, useMantineTheme } from "@mantine/core"
import { useLocalStorage } from "@mantine/hooks"
import { IconChevronDown, IconChevronRight, IconCircle, IconCircleCheckFilled } from "@tabler/icons-react"
import moment from "moment"

import { useProgramGroupsQuery, useProgramSessionsByProgramIdsQuery, useProgramsQuery } from "../../api/program.api"
import { useProgramSessionByIdQuery, useProgramSessionEnrollmentsByIdQuery } from "../../api/programsession.api"
import ProgramSessionCheckIn from "../../Components/CheckIn/ProgramSessionCheckIn"
import { ProgramSession } from "../../models/programs.models"
import { useProgramRegistrationState } from "../../state/programregistration.state"
import { getProgram } from "../../functions/program.functions"

// interface ViewCheckInPageParams {
// 	programSessionId: string;
// }

const ViewCheckInPage = () => {
	const location = useLocation();
	const history = useHistory();
	const urlParams = new URLSearchParams(location.search);
	const theme = useMantineTheme();
	const colorScheme = useMantineColorScheme();
	const autoRefresh = import.meta.env.MODE != 'development';
	// const { programSessionId } = useParams<ViewCheckInPageParams>();
	const programSessionId = urlParams.get("programSessionId");
	const { data: programs, isLoading: isLoadingPrograms } = useProgramsQuery();
	const { data: programGroups } = useProgramGroupsQuery();
	const [selectedProgramSessionId, setSelectedProgramSessionId] = useState<string | null>(programSessionId);
	const { data: programSession, isLoading: isLoadingSession } = useProgramSessionByIdQuery(Number(selectedProgramSessionId ?? 0));
	const { data: programSessionEnrollments, isLoading: isLoadingSessionEnrollments } = useProgramSessionEnrollmentsByIdQuery(programSession?.Id ?? 0, autoRefresh);
	const [selectedProgramGroup, setSelectedProgramGroup] = useLocalStorage({ key: 'calendar-view-program-group', defaultValue: 0 });
	const [selectedProgramId, setSelectedProgramId] = useState<string | null>(urlParams.get("programId"));
	const { setProgramRegistration } = useProgramRegistrationState();

	const formatProgramSessionLabel = (session: ProgramSession) => {
		if (moment(session.StartDate).isSame(moment(session.EndDate), "date")) {
			return `${moment(session.StartDate).format("LL")}, ${moment(session.StartDate).format("h:mma")} - ${moment(session.EndDate).format("h:mma")}`;
		}

		return `${moment(session.StartDate).format("LL")}, ${moment(session.StartDate).format("h:mma")} - ${moment(session.EndDate).format("LL")}, ${moment(session.EndDate).format("h:mma")}`;
	}

	const getProgramSessionDistance = (session: ProgramSession) => {
		const today = moment();
		const start = moment(session.StartDate);
		const end = moment(session.EndDate);

		if (today.isBetween(start, end, undefined, "[]")) {
			return 0;
		}

		if (today.isBefore(start)) {
			return start.diff(today);
		}

		return today.diff(end);
	}

	const isProgramSessionInCurrentYear = (session: ProgramSession) => {
		const currentYearStart = moment().startOf("year");
		const currentYearEnd = moment().endOf("year");
		const start = moment(session.StartDate);
		const end = moment(session.EndDate);

		return start.isSameOrBefore(currentYearEnd) && end.isSameOrAfter(currentYearStart);
	}

	const updateQueryParam = (key: string, value: string | null) => {
		const params = new URLSearchParams(location.search);
		if (value) {
			params.set(key, value);
		} else {
			params.delete(key);
		}

		history.push({ pathname: location.pathname, search: params.toString() });
	}

	const handleProgramChange = (value: string | null) => {
		setSelectedProgramId(value);
		setSelectedProgramSessionId(null);

		const params = new URLSearchParams(location.search);
		if (value) {
			params.set("programId", value);
		} else {
			params.delete("programId");
		}
		params.delete("programSessionId");

		history.push({ pathname: location.pathname, search: params.toString() });
	}

	const handleProgramGroupChange = (value: number) => {
		setSelectedProgramGroup(value);

		const selectedProgram = programs?.find((program) => program.Id === Number(selectedProgramId));
		if (!selectedProgram) {
			return;
		}

		if (value > 0 && selectedProgram.ProgramGroupId !== value) {
			handleProgramChange(null);
		}
	}

	const handleProgramSessionChange = (value: string | null) => {
		setSelectedProgramSessionId(value);
		updateQueryParam("programSessionId", value);
	}
	
	const availablePrograms = useMemo(() => programs?.filter((program) => selectedProgramGroup > 0 ? program.ProgramGroupId === selectedProgramGroup : true) ?? [], [programs, selectedProgramGroup]);
	const programSelectData = useMemo(() => availablePrograms.map((program) => ({ label: program.Title, value: program.Id.toString() })), [availablePrograms]);
	const programIds = availablePrograms.filter((program) => selectedProgramId ? program.Id === Number(selectedProgramId) : true).map((program) => program.Id);
	const { data: sessions, isLoading: isLoadingSessions } = useProgramSessionsByProgramIdsQuery(programIds);
	const selectedProgramSessions = useMemo(() => {
		return sessions?.filter((session) => selectedProgramId ? session.ProgramId === Number(selectedProgramId) && isProgramSessionInCurrentYear(session) : false) ?? [];
	}, [selectedProgramId, sessions]);
	const orderedProgramSessions = useMemo(() => {
		const currentAndFutureSessions = selectedProgramSessions
			.filter((session) => moment(session.EndDate).isSameOrAfter(moment(), "day"))
			.sort((left, right) => getProgramSessionDistance(left) - getProgramSessionDistance(right) || moment(left.StartDate).diff(moment(right.StartDate)));

		const pastSessions = selectedProgramSessions
			.filter((session) => moment(session.EndDate).isBefore(moment(), "day"))
			.sort((left, right) => moment(right.EndDate).diff(moment(left.EndDate)));

		return {
			currentAndFutureSessions,
			pastSessions,
		};
	}, [selectedProgramSessions]);
	const programSessionSelectData = useMemo(() => {
		const selectData: { group: string, items: { label: string, value: string }[] }[] = [];

		if (orderedProgramSessions.currentAndFutureSessions.length > 0) {
			selectData.push({
				group: "Current & Upcoming Sessions",
				items: orderedProgramSessions.currentAndFutureSessions.map((session) => ({
					label: formatProgramSessionLabel(session),
					value: session.Id.toString(),
				}))
			});
		}

		if (orderedProgramSessions.pastSessions.length > 0) {
			selectData.push({
				group: "Past Sessions",
				items: orderedProgramSessions.pastSessions.map((session) => ({
					label: formatProgramSessionLabel(session),
					value: session.Id.toString(),
				}))
			});
		}

		return selectData;
	}, [orderedProgramSessions]);


	const isLoading = isLoadingPrograms || isLoadingSessions;

	useEffect(() => {
		// if (events.length > 0) {
		// 	setSelectedDateRange({ start: defaultSelectedStartDate, end: defaultSelectedEndDate });
		// 	from = defaultSelectedStartDate;
		// 	to = defaultSelectedEndDate;
		// 	let queryString = `from=${moment(defaultSelectedStartDate).toISOString()}&to=${moment(defaultSelectedEndDate).toISOString()}`;

		// 	const calendarApi = calendarRef?.current?.getApi();
		// 	calendarApi?.gotoDate(from);

		// 	setTimeout(() => {
		// 		const calendarApi = calendarRef?.current?.getApi();
		// 		calendarApi?.gotoDate(from);
		// 	}, 100);
		// }
	}, [isLoadingSessions]);

	useEffect(() => {
		const nextProgramId = new URLSearchParams(location.search).get("programId");
		if ((nextProgramId ?? null) !== selectedProgramId) {
			setSelectedProgramId(nextProgramId);
		}
	}, [location.search, selectedProgramId]);

	useEffect(() => {
		const nextProgramSessionId = new URLSearchParams(location.search).get("programSessionId");
		if ((nextProgramSessionId ?? null) !== selectedProgramSessionId) {
			setSelectedProgramSessionId(nextProgramSessionId);
		}
	}, [location.search, selectedProgramSessionId]);

	useEffect(() => {
		if (selectedProgramId == null || programs == null) {
			return;
		}

		const selectedProgram = programs.find((program) => program.Id === Number(selectedProgramId));
		if (!selectedProgram) {
			handleProgramChange(null);
			return;
		}

		if (selectedProgramGroup !== selectedProgram.ProgramGroupId) {
			setSelectedProgramGroup(selectedProgram.ProgramGroupId);
		}
	}, [programs, selectedProgramGroup, selectedProgramId, setSelectedProgramGroup]);

	useEffect(() => {
		if (selectedProgramId == null) {
			return;
		}

		const hasSelectedProgram = availablePrograms.some((program) => program.Id === Number(selectedProgramId));
		if (!hasSelectedProgram) {
			return;
		}
	}, [availablePrograms, selectedProgramId]);

	useEffect(() => {
		if (isLoadingPrograms || isLoadingSessions) {
			return;
		}

		if (selectedProgramId == null) {
			if (selectedProgramSessionId !== null) {
				handleProgramSessionChange(null);
			}
			return;
		}

		if (selectedProgramSessions.length === 0) {
			if (selectedProgramSessionId !== null) {
				handleProgramSessionChange(null);
			}
			return;
		}

		const hasSelectedSession = selectedProgramSessions.some((session) => session.Id === Number(selectedProgramSessionId));
		if (hasSelectedSession) {
			return;
		}

		const defaultSession = orderedProgramSessions.currentAndFutureSessions[0] ?? orderedProgramSessions.pastSessions[0] ?? null;
		if (defaultSession) {
			handleProgramSessionChange(defaultSession.Id.toString());
		}
	}, [isLoadingPrograms, isLoadingSessions, orderedProgramSessions, selectedProgramId, selectedProgramSessionId, selectedProgramSessions]);



	// const handleEventClick = (info: any) => {
		
	// 	var selectedProgramSession = info.event.extendedProps.session as ProgramSession;

	// 	history.push({ pathname: `/calendar/session/${selectedProgramSession.Id}`});
	// }

	return <>
		<Group justify="space-between" align="start">
			<Stack gap={0}>
				<Group gap={5}>
					<Anchor component={Link} to="/" fz="sm" fw="bold">Home</Anchor>
					<IconChevronRight size="14" />
				</Group>
				{import.meta.env.VITE_ENABLE_ACTIVITY_CHECKIN === "true" ? 
					<Title order={1}>Program Check In &amp; Out</Title> :
					<Title order={1}>Check In &amp; Out</Title>
				}
			</Stack>
			<Group align="start">
				
				<Menu shadow="md" position="bottom-end">
					<Menu.Target>
						<Button variant="filled">
							{selectedProgramGroup === 0 ? "All Programs" : programGroups?.find(p => p.Id === selectedProgramGroup)?.Title} <IconChevronDown />
						</Button>
					</Menu.Target>
					<Menu.Dropdown>
						<Menu.Item leftSection={selectedProgramGroup === 0 ? <IconCircleCheckFilled /> : <IconCircle />} onClick={() => setTimeout(() => handleProgramGroupChange(0), 100)}><Text inherit fw={selectedProgramGroup === 0 ? "bold":"normal"}>All Programs</Text></Menu.Item>
						{programGroups?.map((programGroup, index) => {
							return <Menu.Item key={index} leftSection={selectedProgramGroup === programGroup.Id ? <IconCircleCheckFilled /> : <IconCircle />} onClick={() => setTimeout(() => handleProgramGroupChange(programGroup.Id), 100)}><Text inherit fw={selectedProgramGroup === programGroup.Id ? "bold":"normal"}>{programGroup.Title}</Text></Menu.Item>
						})}
					</Menu.Dropdown>
				</Menu>
			</Group>
			{/* <Button component={Link} to="/registrations" size="sm" sx={{ alignSelf: 'flex-start'}} mb="sm">See All Registrations...</Button> */}
		</Group>
	
		<Group mt="md">
			<Select
				data={programSelectData}
				value={selectedProgramId}
				onChange={handleProgramChange}
				placeholder="Select program..."
				clearable
				w={260}
			/>
			<Select
				data={programSessionSelectData}
				value={selectedProgramSessionId}
				onChange={handleProgramSessionChange}
				placeholder="Select session..."
				disabled={!selectedProgramId || selectedProgramSessions.length === 0}
				clearable
				w={380}
				nothingFoundMessage={selectedProgramId ? "No sessions found" : "Select a program first"}
			/>			
		</Group>

		<Divider my="md" />

		<ProgramSessionCheckIn
			enrollments={programSessionEnrollments ?? []}
			isLoading={isLoading || isLoadingSession || isLoadingSessionEnrollments}
			programSessionId={Number(selectedProgramSessionId ?? 0)}
		/>

	</>
}

export default ViewCheckInPage